const API_BASE = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/$/, '')
let csrfToken = ''

export class ApiError extends Error {
  constructor(public status: number, message: string, public unavailable = false, public code = '', public meta: unknown = undefined, public retryable = false) { super(message) }
}
function formatApiMessage(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return value.map(formatApiMessage).filter(Boolean).join('；')
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>
    if (record.msg !== undefined) {
      const location = Array.isArray(record.loc) ? record.loc.filter(item => typeof item === 'string' || typeof item === 'number').join('.') : ''
      const message = formatApiMessage(record.msg)
      return location ? `${location}：${message}` : message
    }
    for (const key of ['message', 'error', 'detail']) {
      if (record[key] !== undefined && record[key] !== value) return formatApiMessage(record[key])
    }
    try { return JSON.stringify(value) }
    catch { return '' }
  }
  return ''
}
export function setCsrfToken(value?: string) { csrfToken = value || '' }
export function apiUrl(path: string) { return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}` }

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes((init.method || 'GET').toUpperCase())) headers.set('X-CSRF-Token', csrfToken)
  let response: Response
  try { response = await fetch(apiUrl(path), { ...init, headers, credentials: 'include' }) }
  catch { throw new ApiError(0, '无法连接 Biliup 服务，请确认后端已启动。', true) }
  const raw = await response.text()
  let payload: unknown = null
  try { payload = raw ? JSON.parse(raw) : null } catch { payload = raw }
  if (!response.ok) {
    const envelope = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {}
    const detail = envelope.detail && typeof envelope.detail === 'object' ? envelope.detail as Record<string, unknown> : null
    const message = detail?.message || detail?.error || envelope.error || envelope.detail || `请求失败（${response.status}）`
    const code = String(detail?.code || detail?.error_code || envelope.code || envelope.error_code || '')
    const meta = detail?.meta ?? envelope.meta
    const retryable = Boolean(detail?.retryable ?? envelope.retryable ?? response.status >= 500)
    throw new ApiError(response.status, formatApiMessage(message) || `请求失败（${response.status}）`, response.status >= 500, code, meta, retryable)
  }
  // 工作区接口使用后端统一的 {ok, data, ...} 包装，任务/SSE 接口则有意直接
  // 返回传输载荷。页面 API 应按实际数据契约定义类型，不要强迫每个页面理解包装层。
  if (payload && typeof payload === 'object' && 'ok' in payload && 'data' in payload) {
    const envelope = payload as { ok: boolean; data?: unknown; error?: string; code?: string; meta?: unknown }
    if (!envelope.ok) throw new ApiError(response.status, envelope.error || '请求失败', false, String(envelope.code || ''), envelope.meta)
    return envelope.data as T
  }
  return payload as T
}

export interface SseEvent { type: string; [key: string]: unknown }
export type StreamSseResult = 'completed' | 'aborted'

/** 启动一次运行，并消费 POST 直接返回的 text/event-stream 响应。 */
export async function streamSse(path: string, body: unknown, onEvent: (event: SseEvent) => void, signal?: AbortSignal): Promise<StreamSseResult> {
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' })
  if (csrfToken) headers.set('X-CSRF-Token', csrfToken)
  let response: Response
  try {
    response = await fetch(apiUrl(path), { method: 'POST', body: JSON.stringify(body), headers, credentials: 'include', signal })
  } catch (error) {
    if ((error as Error).name === 'AbortError') return 'aborted'
    throw new ApiError(0, '无法连接 Biliup 服务。', true)
  }
  if (!response.ok) {
    let message = '流式请求不可用'
    let code = ''
    let meta: unknown
    let retryable = response.status >= 500
    try {
      const payload = await response.clone().json() as Record<string, unknown>
      const detail = payload.detail && typeof payload.detail === 'object' ? payload.detail as Record<string, unknown> : null
      if (detail?.message || detail?.error || payload.detail || payload.error) message = formatApiMessage(detail?.message || detail?.error || payload.detail || payload.error)
      code = String(detail?.code || detail?.error_code || payload.code || payload.error_code || '')
      meta = detail?.meta ?? payload.meta
      retryable = Boolean(detail?.retryable ?? payload.retryable ?? retryable)
    } catch { /* 保留传输层兜底 */ }
    throw new ApiError(response.status, message, response.status >= 500, code, meta, retryable)
  }

  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('text/event-stream') || !response.body) throw new ApiError(response.status, '服务未返回直接 SSE 流', response.status >= 500)
  try {
    await consumeSse(response.body, onEvent)
    return 'completed'
  } catch (error) {
    if ((error as Error).name === 'AbortError') return 'aborted'
    throw error
  }
}

/** 连接 GET text/event-stream 接口，并持续消费直到完成。 */
export async function streamSseGet(path: string, onEvent: (event: SseEvent) => void, signal?: AbortSignal): Promise<StreamSseResult> {
  const headers = new Headers()
  let response: Response
  try {
    headers.set('Accept', 'text/event-stream')
    response = await fetch(apiUrl(path), { headers, credentials: 'include', signal })
  } catch (error) {
    if ((error as Error).name === 'AbortError') return 'aborted'
    throw new ApiError(0, '无法连接 Biliup 服务。', true)
  }
  if (!response.ok) throw new ApiError(response.status, `事件流不可用（${response.status}）`, response.status >= 500)
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('text/event-stream') || !response.body) throw new ApiError(response.status, '服务未返回事件流', response.status >= 500)
  try {
    await consumeSse(response.body, onEvent)
    return 'completed'
  } catch (error) {
    if ((error as Error).name === 'AbortError') return 'aborted'
    throw error
  }
}

function streamMessageText(value: Record<string, unknown>) {
  const content = Array.isArray(value.content) ? value.content : value.content_blocks
  if (typeof content === 'string') return content
  if (!Array.isArray(content)) return ''
  return content.reduce((result, item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return result
    const block = item as Record<string, unknown>
    if (block.type !== 'text' && block.type !== 'output_text') return result
    const text = typeof block.text === 'string' ? block.text : ''
    return result + text
  }, '')
}

function streamMessageReasoning(value: Record<string, unknown>) {
  const content = Array.isArray(value.content) ? value.content : value.content_blocks
  if (!Array.isArray(content)) return ''
  return content.map(item => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return ''
    const block = item as Record<string, unknown>
    if (block.type !== 'reasoning') return ''
    if (typeof block.reasoning === 'string') return block.reasoning
    const summary = Array.isArray(block.summary) ? block.summary : []
    return summary.map(part => {
      if (!part || typeof part !== 'object' || Array.isArray(part)) return ''
      const text = (part as Record<string, unknown>).text
      return typeof text === 'string' ? text : ''
    }).join('')
  }).join('')
}

export function langGraphPartEvent(part: unknown, sequence?: number): SseEvent | undefined {
  if (!part || typeof part !== 'object' || Array.isArray(part)) return undefined
  const parsed = part as Record<string, unknown>
  const streamType = String(parsed.type || '')
  if (streamType === 'messages') {
    const data = Array.isArray(parsed.data) ? parsed.data : []
    const chunk = data[0] && typeof data[0] === 'object' && !Array.isArray(data[0]) ? data[0] as Record<string, unknown> : undefined
    if (!chunk) return undefined
    const metadata = data[1] && typeof data[1] === 'object' && !Array.isArray(data[1]) ? data[1] as Record<string, unknown> : {}
    const content = streamMessageText(chunk)
    const reasoningDelta = streamMessageReasoning(chunk)
    return {
      ...metadata,
      type: 'message',
      stream_type: 'messages',
      role: 'assistant',
      content,
      reasoning_delta: reasoningDelta || undefined,
      message_chunk: chunk,
      seq: sequence,
      message: '',
    }
  }
  if (streamType === 'custom') {
    const data = parsed.data && typeof parsed.data === 'object' && !Array.isArray(parsed.data) ? parsed.data as Record<string, unknown> : undefined
    if (!data || typeof data.type !== 'string' || !data.type) return undefined
    return { ...data, type: data.type, seq: sequence }
  }
  return undefined
}

async function consumeSse(body: ReadableStream<Uint8Array>, onEvent: (event: SseEvent) => void) {
  const reader = body.getReader(); const decoder = new TextDecoder(); let buffer = ''
  let dataLines: string[] = []; let eventId: string | undefined
  const dispatchEvent = () => {
    if (!dataLines.length) { eventId = undefined; return }
    const data = dataLines.join('\n'); const rawId = eventId
    dataLines = []; eventId = undefined
    try {
      const parsed = JSON.parse(data) as Record<string, unknown>
      const parsedId = rawId === undefined ? undefined : Number(rawId)
      const sequence = parsedId !== undefined && Number.isFinite(parsedId) ? parsedId : undefined
      const standard = langGraphPartEvent(parsed, sequence)
      if (standard) onEvent(standard)
    } catch {
      throw new Error('LangGraph SSE 数据无效')
    }
  }
  const processLine = (line: string) => {
    if (!line) { dispatchEvent(); return }
    if (line.startsWith(':')) return
    const separator = line.indexOf(':')
    const field = separator < 0 ? line : line.slice(0, separator)
    let value = separator < 0 ? '' : line.slice(separator + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'data') dataLines.push(value)
    else if (field === 'id') eventId = value
  }
  const processText = (text: string) => {
    buffer += text
    while (true) {
      const separator = /\r\n|\r|\n/.exec(buffer)
      if (!separator || separator.index === undefined) return
      processLine(buffer.slice(0, separator.index))
      buffer = buffer.slice(separator.index + separator[0].length)
    }
  }
  while (true) {
    const { value, done } = await reader.read(); if (done) break
    processText(decoder.decode(value, { stream: true }))
  }
  processText(decoder.decode())
  if (buffer) { processLine(buffer); buffer = '' }
  dispatchEvent()
}
